import i18n from "@/i18n";
/** WORKFORCE-EXTERNAL-EXTRAS-QR-V1 — pure helpers (public form + manager). */

export const EXTRA_FORM_PATH = "/shift-report/";

export function extraFormUrl(origin, token) {
    return token ? `${origin}${EXTRA_FORM_PATH}${token}` : "";
}

export function newSubmissionId() {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
    return `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

export function isOvernight(start, end) {
    return Boolean(start && end && end < start);
}

export function formatMinutes(min) {
    if (min == null) return "—";
    const h = Math.floor(min / 60);
    const m = min % 60;
    return m ? `${h} h ${m} min` : `${h} h`;
}

export function buildExtraSubmission(f, submissionId) {
    return {
        name: (f.name || "").trim(),
        agency: (f.agency || "").trim(),
        shift_date: f.shiftDate,
        start_time: f.startTime,
        end_time: f.endTime,
        staff_meal: f.staffMeal,
        finished_as_expected: f.finishedAsExpected,
        client_submission_id: submissionId,
    };
}

export function extraFormMissing(f) {
    const out = [];
    if ((f.name || "").trim().length < 2) out.push("your name");
    if ((f.agency || "").trim().length < 2) out.push("your agency or company");
    if (!f.shiftDate) out.push("the shift date");
    if (!f.startTime || !f.endTime) out.push("start and end time");
    if (f.startTime && f.startTime === f.endTime) out.push("an end time different from the start");
    if (typeof f.staffMeal !== "boolean") out.push("whether you ate a staff meal");
    if (typeof f.finishedAsExpected !== "boolean") out.push("whether you finished at the expected time");
    return out;
}

const MESSAGES = {
    get EXTRA_FORM_NOT_AVAILABLE() { return i18n.t("wf.externalExtrasHelpers.MESSAGES.EXTRA_FORM_NOT_AVAILABLE", "This shift-report link is no longer active. Ask the restaurant for the current QR code."); },
    get EXTRA_NAME_INVALID() { return i18n.t("wf.externalExtrasHelpers.MESSAGES.EXTRA_NAME_INVALID", "Enter your name (2–80 characters)."); },
    get EXTRA_AGENCY_INVALID() { return i18n.t("wf.externalExtrasHelpers.MESSAGES.EXTRA_AGENCY_INVALID", "Enter the agency or company you work for (2–80 characters)."); },
    get EXTRA_SHIFT_DATE_INVALID() { return i18n.t("wf.externalExtrasHelpers.MESSAGES.EXTRA_SHIFT_DATE_INVALID", "Choose a shift date from the last 7 days."); },
    get EXTRA_TIME_RANGE_INVALID() { return i18n.t("wf.externalExtrasHelpers.MESSAGES.EXTRA_TIME_RANGE_INVALID", "Check the start and end times."); },
    get EXTRA_SHIFT_TOO_LONG() { return i18n.t("wf.externalExtrasHelpers.MESSAGES.EXTRA_SHIFT_TOO_LONG", "A shift can be at most 16 hours. Check the times."); },
    get EXTRA_SHIFT_NOT_FINISHED() { return i18n.t("wf.externalExtrasHelpers.MESSAGES.EXTRA_SHIFT_NOT_FINISHED", "Send the report when your shift has ended."); },
    get EXTRA_ANSWER_REQUIRED() { return i18n.t("wf.externalExtrasHelpers.MESSAGES.EXTRA_ANSWER_REQUIRED", "Answer both questions."); },
    get EXTRA_RATE_LIMITED() { return i18n.t("wf.externalExtrasHelpers.MESSAGES.EXTRA_RATE_LIMITED", "Too many reports from this device. Try again later."); },
    get EXTRA_REVIEW_REASON_REQUIRED() { return i18n.t("wf.externalExtrasHelpers.MESSAGES.EXTRA_REVIEW_REASON_REQUIRED", "Add a short reason."); },
    get EXTRA_REPORT_REVISION_CONFLICT() { return i18n.t("wf.externalExtrasHelpers.MESSAGES.EXTRA_REPORT_REVISION_CONFLICT", "Someone else changed this report. Reload and try again."); },
    get EXTRA_REVIEW_STATE_INVALID() { return i18n.t("wf.externalExtrasHelpers.MESSAGES.EXTRA_REVIEW_STATE_INVALID", "This action is not available for the report's current status."); },
    get EXTRA_CORRECTION_EMPTY() { return i18n.t("wf.externalExtrasHelpers.MESSAGES.EXTRA_CORRECTION_EMPTY", "Change at least one value."); },
    get HOURS_SOURCE_DATE_INVALID() { return i18n.t("wf.externalExtrasHelpers.MESSAGES.HOURS_SOURCE_DATE_INVALID", "Choose a valid start date."); },
    get HOURS_SOURCE_REASON_REQUIRED() { return i18n.t("wf.externalExtrasHelpers.MESSAGES.HOURS_SOURCE_REASON_REQUIRED", "Add a short reason."); },
};

export function extraErrorMessage(err, fallback = "Could not send. Check your connection and try again.") {
    const code = err?.response?.data?.detail?.code;
    return MESSAGES[code] || fallback;
}

export const EXTRA_STATUS_LABEL = { SUBMITTED: "Waiting for review", APPROVED: "Approved", REJECTED: "Rejected" };

export const HOURS_REASON_TEXT = {
    get EMPLOYEE_ACTUALS_UNVERIFIED() { return i18n.t("wf.externalExtrasHelpers.HOURS_REASON_TEXT.EMPLOYEE_ACTUALS_UNVERIFIED", "employee hours not yet reviewed"); },
    get EMPLOYEE_HOURS_NOT_CONFIRMED() { return i18n.t("wf.externalExtrasHelpers.HOURS_REASON_TEXT.EMPLOYEE_HOURS_NOT_CONFIRMED", "no confirmed employee hours (period not closed)"); },
    get EXTERNAL_REPORTS_PENDING_REVIEW() { return i18n.t("wf.externalExtrasHelpers.HOURS_REASON_TEXT.EXTERNAL_REPORTS_PENDING_REVIEW", "extra reports waiting for review"); },
    get MANUAL_HOURS_MISSING() { return i18n.t("wf.externalExtrasHelpers.HOURS_REASON_TEXT.MANUAL_HOURS_MISSING", "manual worked hours not entered"); },
};

/** Totals of the automatic hours breakdown (minutes → hours). */
export function hoursTotals(days) {
    const t = { employee: 0, external: 0, incomplete: [] };
    (days || []).forEach((d) => {
        t.employee += d.employee_minutes;
        t.external += d.external_minutes;
        if (!d.complete) t.incomplete.push(d);
    });
    return { employeeHours: t.employee / 60, externalHours: t.external / 60,
        totalHours: (t.employee + t.external) / 60, incomplete: t.incomplete };
}

export function fmtHours(h) {
    return `${(Math.round(h * 100) / 100).toFixed(2)} h`;
}
